import { Entity, FieldType, getFilter, getRowDataFromKey, PersistenceType } from 'gossamer-unified';
import { Institution } from './Institution';

export interface ReportInstitutionViewRow {
  institution_id: number;
  week_commencing: string;
  event_count: number; // total events
  post_event_count: number; // total events with flow_option IN 'attendanceOnly', 'full'
  booking_count: number;
  post_event_booking_count: number; // total bookings for above events
  booking_rate: number;
  capacity: number;
  utilisation_rate: string;
  attendance_count: number;
  attendance_rate: string;
  sum_content_score: number;
  avg_content_score: number;
  sum_delivery_score: number;
  avg_delivery_score: number;
  sum_admin_score: number;
  avg_admin_score: number;
  sum_overall_score: number;
  avg_overall_score: number;
  feedback_count: number;
}

export const ReportInstitutionView: Entity<ReportInstitutionViewRow> = {
  id: 'ReportInstitutionView',
  title: 'Report Institution View',
  service: 'main',
  rowLabel: (row: ReportInstitutionViewRow) => [row.institution_id, row.week_commencing].join(' / '),
  primaryKeyFields: ['institution_id', 'week_commencing'],
  source: {
    tableName: 'public.report_institution_view',
  },
  security: [
    { role: 'admin', action: 'read', access: 'conditional', condition: 'institution_id = {adminInstitutionId}' },
    { role: 'staff', action: 'read', access: 'all' },
  ],
  defaultSort: [['week_commencing', 'DESC']],
  defaultFilters: [
    { fieldId: 'institution_id', operator: 'eq', value: '' },
    { fieldId: 'week_commencing', operator: 'eq', value: '' },
  ],
  listRowLink: (key: string) => {
    const { institution_id, week_commencing } = getRowDataFromKey(ReportInstitutionView, key);
    const start = new Date(week_commencing as string);
    const end = new Date(week_commencing as string);
    end.setDate(end.getDate() + 7);
    return (
      '/admin/reporting/event-search?q=' +
      encodeURIComponent(
        JSON.stringify({
          where: {
            and: [
              getFilter('institution_id', 'eq', institution_id),
              getFilter('start_at', 'gt', start.toISOString()),
              getFilter('start_at', 'lt', end.toISOString()),
            ],
          },
        }),
      )
    );
  },
  fields: {
    institution_id: {
      seq: 5,
      label: 'Institution',
      type: FieldType.Reference,
      refEntity: Institution,
    },
    week_commencing: {
      seq: 10,
      type: FieldType.Date,
      label: 'Week Commencing',
      listColumn: true,
      listColumnAlign: 'right',
      listColumnWidth: 120,
    },
    event_count: {
      seq: 15,
      type: FieldType.Integer,
      label: 'All Events',
      listColumn: true,
    },
    booking_count: {
      seq: 20,
      label: 'All Bookings',
      type: FieldType.Integer,
      listColumn: true,
    },
    booking_rate: {
      seq: 30,
      label: 'Booking Rate',
      type: FieldType.Decimal,
      listColumn: true,
      decimalDigits: 2,
      persistence: PersistenceType.FrontEndOnly,
      evaluateFrontEndIn: (row: ReportInstitutionViewRow) =>
        !row.event_count ? 0 : row.booking_count / row.event_count,
    },
    capacity: {
      seq: 35,
      label: 'Capacity',
      type: FieldType.Integer,
      listColumn: true,
    },
    utilisation_rate: {
      seq: 40,
      label: 'Utilisation Rate',
      type: FieldType.Text,
      listColumn: true,
      listColumnAlign: 'right',
      persistence: PersistenceType.FrontEndOnly,
      evaluateFrontEndIn: (row: ReportInstitutionViewRow) =>
        !row.capacity ? '-' : ((row.booking_count * 100) / row.capacity).toFixed(0) + '%',
    },
    post_event_count: {
      seq: 45,
      type: FieldType.Integer,
      label: 'Tracked Events',
      listColumn: true,
    },
    post_event_booking_count: {
      seq: 47,
      label: 'Tracked Bookings',
      type: FieldType.Integer,
      listColumn: true,
    },
    attendance_count: {
      seq: 50,
      label: 'Attendances',
      type: FieldType.Integer,
      listColumn: true,
    },
    attendance_rate: {
      seq: 55,
      label: 'Attendance Rate',
      type: FieldType.Text,
      listColumn: true,
      listColumnAlign: 'right',
      persistence: PersistenceType.FrontEndOnly,
      evaluateFrontEndIn: (row: ReportInstitutionViewRow) =>
        !row.post_event_booking_count
          ? '-'
          : ((row.attendance_count * 100) / row.post_event_booking_count).toFixed(0) + '%',
    },
    sum_content_score: {
      seq: 65,
      label: 'Total Content Score',
      type: FieldType.Decimal,
    },
    avg_content_score: {
      seq: 70,
      label: 'Average Content Score',
      type: FieldType.Decimal,
      listColumnAlign: 'right',
      persistence: PersistenceType.FrontEndOnly,
      evaluateFrontEndIn: (row: ReportInstitutionViewRow) =>
        !row.feedback_count ? '-' : (row.sum_content_score / row.feedback_count).toFixed(1),
    },
    sum_delivery_score: {
      seq: 75,
      label: 'Total Delivery Score',
      type: FieldType.Decimal,
    },
    avg_delivery_score: {
      seq: 80,
      label: 'Average Delivery Score',
      type: FieldType.Decimal,
      listColumnAlign: 'right',
      persistence: PersistenceType.FrontEndOnly,
      evaluateFrontEndIn: (row: ReportInstitutionViewRow) =>
        !row.feedback_count ? '-' : (row.sum_delivery_score / row.feedback_count).toFixed(1),
    },
    sum_admin_score: {
      seq: 85,
      label: 'Total Admin Score',
      type: FieldType.Decimal,
    },
    avg_admin_score: {
      seq: 90,
      label: 'Average Admin Score',
      type: FieldType.Decimal,
      listColumnAlign: 'right',
      persistence: PersistenceType.FrontEndOnly,
      evaluateFrontEndIn: (row: ReportInstitutionViewRow) =>
        !row.feedback_count ? '-' : (row.sum_admin_score / row.feedback_count).toFixed(1),
    },
    sum_overall_score: {
      seq: 95,
      label: 'Total Overall Score',
      type: FieldType.Decimal,
    },
    avg_overall_score: {
      seq: 100,
      label: 'Average Overall Score',
      type: FieldType.Decimal,
      listColumnAlign: 'right',
      persistence: PersistenceType.FrontEndOnly,
      evaluateFrontEndIn: (row: ReportInstitutionViewRow) =>
        !row.feedback_count ? '-' : (row.sum_overall_score / row.feedback_count).toFixed(1),
    },
    feedback_count: {
      seq: 105,
      label: 'Feedback',
      type: FieldType.Integer,
      listColumn: true,
    },
  },
};
